import { ChannelIcon, type Channel } from './channel-icons'

// One incident, told across three channels. The wording follows what the server really
// sends (uptrack-server, crates/notifications): "🚨 {name} is DOWN" with a reason and the
// failure count, "✅ {name} is back UP" with Started and Resolved, and the early warning
// "⚠️ {name} may be degraded — N failed check" with "No incident opened yet". Example data.
type AlertState = 'down' | 'up' | 'degraded'

const ALERTS: { channel: Channel; to: string; time: string; state: AlertState; emoji: string; title: string; detail: string }[] = [
  {
    channel: 'slack',
    to: '#on-call',
    time: '14:02',
    state: 'down',
    emoji: '🚨',
    title: 'checkout-flow is DOWN',
    detail: 'Click “Pay now” timed out · 3 failures',
  },
  {
    channel: 'telegram',
    to: 'Uptrack alerts',
    time: '14:09',
    state: 'up',
    emoji: '✅',
    title: 'checkout-flow is back UP',
    detail: 'Started 14:02 · Resolved 14:09',
  },
  {
    channel: 'discord',
    to: '#incidents',
    time: '14:11',
    state: 'degraded',
    emoji: '⚠️',
    title: 'api.example.com may be degraded',
    detail: '1 failed check · no incident opened yet',
  },
]

// The coloured edge shows the alert's status, as it does in Slack and Discord.
const ACCENT: Record<AlertState, string> = {
  down: 'border-l-status-down',
  up: 'border-l-status-up',
  degraded: 'border-l-status-degraded',
}

/**
 * A stack of alert cards, one per channel. `compact` is a two-line card for small
 * pieces. `responsive` shows only the first card below the xl breakpoint, so a
 * narrow stage keeps room for the globe.
 */
export function AlertStack({
  compact = false,
  responsive = false,
  className = '',
}: {
  compact?: boolean
  responsive?: boolean
  className?: string
}) {
  return (
    <ul className={`flex flex-col ${compact ? 'gap-1.5' : 'gap-2'} ${className}`}>
      {ALERTS.map((a, i) => {
        const display = responsive && i > 0 ? 'hidden xl:flex' : 'flex'
        const frame = `items-center rounded-lg border border-l-[3px] border-border bg-popover shadow-raised ${ACCENT[a.state]} ${display}`
        const headline = (
          <>
            <span aria-hidden="true">{a.emoji}</span> {a.title}
          </>
        )
        if (compact) {
          return (
            <li key={a.channel} className={`${frame} gap-2.5 p-1.5`}>
              <ChannelIcon channel={a.channel} size="sm" />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-3">
                  <p className="truncate text-xs font-semibold text-foreground">{headline}</p>
                  <p className="shrink-0 num text-xs text-muted-foreground">{a.time}</p>
                </div>
                <p className="truncate text-xs text-body">{a.detail}</p>
              </div>
            </li>
          )
        }
        return (
          <li key={a.channel} className={`${frame} gap-3 p-2.5`}>
            <ChannelIcon channel={a.channel} />
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline justify-between gap-3 text-xs text-muted-foreground">
                <span className="truncate">{a.to}</span>
                <span className="shrink-0 num">{a.time}</span>
              </div>
              <p className="mt-0.5 truncate text-[13px] font-semibold text-foreground">{headline}</p>
              <p className="truncate text-[13px] text-body">{a.detail}</p>
            </div>
          </li>
        )
      })}
    </ul>
  )
}
